import { LOTS, SPOTS, type Vec2 } from '../world/layout';
import { DALE_SLOT, EARL_SLOT, JENKINS_SLOT, MEEMAW_SLOT, SKEETER_SLOT, TAMMY_SLOT } from '../world/rooms';

/**
 * The neighbors' trailers you can walk into while they're up: they're out in the yard,
 * the door's unlocked, and they holler at you to make yourself at home.
 */

export const PARK_HOME_IDS = ['tammy', 'earl', 'dale', 'jenkins', 'skeeter', 'meemaw'] as const;
export type ParkHomeId = (typeof PARK_HOME_IDS)[number];

export interface ParkHomeDef {
  id: ParkHomeId;
  /** The NPC who lives there. */
  npc: ParkHomeId;
  slot: number;
  /** Where you stand to knock (the foot of their steps). */
  stoop: Vec2;
  /** "Big Earl's place". */
  place: string;
  /** What they holler from the yard when you let yourself in. */
  welcome: readonly string[];
  /** The door while they're asleep. */
  closed: string;
}

export const PARK_HOMES: Record<ParkHomeId, ParkHomeDef> = {
  tammy: {
    id: 'tammy',
    npc: 'tammy',
    slot: TAMMY_SLOT,
    stoop: LOTS.tammy.stoop,
    place: "Tammy Lynn's place",
    welcome: [
      "Tammy, from the yard: 'Go on in, hun! Don't touch the inventory. Unless you're BUYING. :hearts:'",
      "Tammy, from the yard: 'Shoes off! The carpet's been blessed with oils.'",
    ],
    closed: "A sign on the door: 'BOSS BABE IS RECHARGING. DM ME ABOUT THE OPPORTUNITY.'",
  },
  earl: {
    id: 'earl',
    npc: 'earl',
    slot: EARL_SLOT,
    stoop: LOTS.earl.stoop,
    place: "Big Earl's place",
    welcome: [
      "Earl, from the grill: 'Door's open! Fridge is fair game. Bottom shelf only.'",
      "Earl, from the grill: 'Make yourself at home! Don't sit in my chair. ...Fine, sit in my chair.'",
    ],
    closed: 'Earl snores through the door like a diesel idling. Tank growls once, politely.',
  },
  dale: {
    id: 'dale',
    npc: 'dale',
    slot: DALE_SLOT,
    stoop: LOTS.dale.stoop,
    place: "Cousin Dale's place",
    welcome: [
      "Dale, from somewhere: 'It's open! If anybody asks, you're my accountant.'",
      "Dale, from somewhere: 'Come on in! Don't open the closet. That's inventory. Legal inventory.'",
    ],
    closed: "Nobody answers. The door has three new locks and a note: 'NOT HOME. NEVER WAS.'",
  },
  jenkins: {
    id: 'jenkins',
    npc: 'jenkins',
    slot: JENKINS_SLOT,
    stoop: LOTS.jenkins.stoop,
    place: "Old Man Jenkins' place",
    welcome: [
      "Jenkins, without looking up: 'Door's open. Wipe your feet. It's 1974 in there and I'd like to keep it that way.'",
      "Jenkins, from his chair: 'Go on. Don't touch the thermostat. I know where it's set. I always know.'",
    ],
    closed: "Jenkins hollers through the door: 'I'M ASLEEP.' He goes to bed at nine. He has for fifty years.",
  },
  skeeter: {
    id: 'skeeter',
    npc: 'skeeter',
    slot: SKEETER_SLOT,
    stoop: LOTS.skeeter.stoop,
    place: "Skeeter's place",
    welcome: [
      "Skeeter, from the ramp: 'Bro, go in! Don't trip on the ring light. That's the studio.'",
      "Skeeter, from the ramp: 'It's open! Smash that like button on the way in. There's no button. It's a vibe.'",
    ],
    closed: 'Skeeter\'s door has a sticky note: "SLEEPING. OR STREAMING. EITHER WAY DONT KNOCK."',
  },
  meemaw: {
    id: 'meemaw',
    npc: 'meemaw',
    slot: MEEMAW_SLOT,
    stoop: SPOTS.meemawStoop,
    place: "Meemaw Pearl's place",
    welcome: [
      "Meemaw, from her rocker: 'Come in, come in, baby. There's candy in the dish. Don't sit on the good couch.'",
      "Meemaw, from her rocker: 'Shoes off! I just vacuumed. I always just vacuumed.'",
    ],
    closed: "Meemaw's lights are off. A cross-stitch on the door says 'GO HOME, IT'S LATE.'",
  },
};

/** Every neighbor who'll have you in (the trophy for snooping through all of them). */
export const NOSY_HOMES = [...PARK_HOME_IDS, 'brenda', 'wanda', 'hank'] as const;

/** Set the first time you're inside each of them. */
export const beenInFlag = (id: string): string => `been_in_${id}`;

/** "9 AM", for doors that say when they open. */
export function hourLabel(hour: number): string {
  const h = ((Math.floor(hour) % 24) + 24) % 24;
  return `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`;
}
